import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TYPES, TYPE_LABELS, getAgreementStatus, formatDate } from "../constants/avtaler";
import AvtaleLogo from "../components/AvtaleLogo";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Lightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <img src={url} alt="Kontrakt" className="max-h-full max-w-full rounded shadow-xl object-contain" onClick={(e) => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full w-8 h-8 flex items-center justify-center text-lg">
        ×
      </button>
    </div>
  );
}

function toDateInputValue(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

// ── Avtaleinfo (redigering) ──────────────────────────────────────────────────
function AgreementInfoForm({ agreement, onAgreementUpdated }) {
  const [name, setName] = useState(agreement.name);
  const [type, setType] = useState(agreement.type);
  const [discountDetails, setDiscountDetails] = useState(agreement.discountDetails);
  const [discountCode, setDiscountCode] = useState(agreement.discountCode || "");
  const [contactInfo, setContactInfo] = useState(agreement.contactInfo || "");
  const [contactPhone, setContactPhone] = useState(agreement.contactPhone || "");
  const [notes, setNotes] = useState(agreement.notes || "");
  const [startDate, setStartDate] = useState(toDateInputValue(agreement.startDate));
  const [expiryDate, setExpiryDate] = useState(toDateInputValue(agreement.expiryDate));
  const [saving, setSaving] = useState(false);

  const dirty = name !== agreement.name
    || type !== agreement.type
    || discountDetails !== agreement.discountDetails
    || discountCode !== (agreement.discountCode || "")
    || contactInfo !== (agreement.contactInfo || "")
    || contactPhone !== (agreement.contactPhone || "")
    || notes !== (agreement.notes || "")
    || startDate !== toDateInputValue(agreement.startDate)
    || expiryDate !== toDateInputValue(agreement.expiryDate);

  async function handleSave() {
    if (!name.trim()) return alert("Navn er påkrevd.");
    if (!discountDetails.trim()) return alert("Rabattbetingelser er påkrevd.");
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, type, discountDetails, discountCode, contactInfo, contactPhone, notes, startDate, expiryDate }),
      });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre avtale.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Kunde/Firma/Bedrift"
          className="p-2 border border-gray-300 rounded-md md:col-span-2 font-semibold text-lg"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Type"
          className="p-2 border border-gray-300 rounded-md"
        >
          {TYPES.map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t]}</option>
          ))}
        </select>
      </div>

      <textarea
        value={discountDetails}
        onChange={(e) => setDiscountDetails(e.target.value)}
        rows={4}
        placeholder="Rabattbetingelser…"
        className="p-2 border border-gray-300 rounded-md"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          placeholder="Rabattkode (valgfritt)"
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="text"
          value={contactInfo}
          onChange={(e) => setContactInfo(e.target.value)}
          placeholder="Kontaktperson (valgfritt)"
          className="p-2 border border-gray-300 rounded-md"
        />
        <input
          type="tel"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="Telefon til kontaktperson (valgfritt)"
          aria-label="Telefon til kontaktperson"
          autoComplete="off"
          className="p-2 border border-gray-300 rounded-md"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Inngått</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Inngått"
            className="p-2 border border-gray-300 rounded-md w-full"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Utløper</label>
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            aria-label="Utløper"
            className="p-2 border border-gray-300 rounded-md w-full"
          />
        </div>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder="Interne notater (valgfritt)…"
        className="p-2 border border-gray-300 rounded-md text-sm"
      />

      {dirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="self-start px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? "Lagrer…" : "Lagre endringer"}
        </button>
      )}
    </div>
  );
}

// ── Kontraktbilder ────────────────────────────────────────────────────────────
// ── Logo ─────────────────────────────────────────────────────────────────────
function Logo({ agreement, onAgreementUpdated }) {
  const [opptatt, setOpptatt] = useState(false);
  const fileRef = useRef();

  async function lastOpp(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpptatt(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}/logo`, { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke laste opp logoen.");
    } finally {
      setOpptatt(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function fjern() {
    if (!window.confirm("Fjerne logoen?")) return;
    setOpptatt(true);
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}/logo`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke fjerne logoen.");
    } finally {
      setOpptatt(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <AvtaleLogo agreement={agreement} storrelse="stor" />
      <div className="flex flex-wrap gap-2">
        <label className={`cursor-pointer text-xs px-3 py-1.5 rounded bg-blue-700 text-white hover:bg-blue-800 focus-within:ring-2 focus-within:ring-blue-600 ${opptatt ? "opacity-60 pointer-events-none" : ""}`}>
          {opptatt ? "Lagrer…" : agreement.logo?.url ? "Bytt logo" : "Last opp logo"}
          <input ref={fileRef} type="file" accept="image/*" className="sr-only" onChange={lastOpp} disabled={opptatt} />
        </label>
        {agreement.logo?.url && (
          <button type="button" onClick={fjern} disabled={opptatt} className="text-xs px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
            Fjern logo
          </button>
        )}
      </div>
    </div>
  );
}

function ContractImages({ agreement, onAgreementUpdated, setLightboxUrl }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}/images`, { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke laste opp bilde.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleCaptionSave(imageId, caption) {
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}/images/${imageId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption }),
      });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre bildetekst.");
    }
  }

  async function handleDeleteImage(imageId) {
    if (!window.confirm("Slett dette bildet?")) return;
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}/images/${imageId}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onAgreementUpdated(await r.json());
    } catch {
      alert("Kunne ikke slette bilde.");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500">Kontraktbilder ({agreement.images.length})</span>
        <label className={`cursor-pointer text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? "Laster opp…" : "+ Legg til bilde"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {agreement.images.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {agreement.images.map((img) => (
            <div key={img._id} className="w-56">
              <div className="relative group w-56 h-36 bg-white rounded border">
                <img
                  src={img.url}
                  alt={img.caption || agreement.name}
                  className="w-full h-full object-contain cursor-pointer hover:opacity-90"
                  onClick={() => setLightboxUrl(img.url)}
                />
                <button
                  onClick={() => handleDeleteImage(img._id)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none items-center justify-center hidden group-hover:flex"
                  title="Slett bilde"
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                defaultValue={img.caption || ""}
                placeholder="Bildetekst"
                onBlur={(e) => { if (e.target.value !== (img.caption || "")) handleCaptionSave(img._id, e.target.value); }}
                className="mt-1 w-full text-xs p-1 border border-gray-200 rounded"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-600">Ingen bilder av kontrakten lagt til ennå.</p>
      )}
    </div>
  );
}

function AvtaleDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [exclusionText, setExclusionText] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    async function fetchAgreement() {
      try {
        const r = await fetch(`${API_BASE_URL}/avtaler/${id}`);
        if (!r.ok) throw new Error();
        setAgreement(await r.json());
      } catch {
        console.error("Kunne ikke hente avtale");
      } finally {
        setLoading(false);
      }
    }
    async function fetchSettings() {
      try {
        const r = await fetch(`${API_BASE_URL}/avtaler/innstillinger`);
        if (!r.ok) throw new Error();
        setExclusionText((await r.json()).exclusionText || "");
      } catch {
        console.error("Kunne ikke hente innstillinger");
      }
    }
    fetchAgreement();
    fetchSettings();
  }, [id]);

  async function handleDelete() {
    if (!window.confirm(`Slette avtalen med "${agreement.name}"? Dette kan ikke angres.`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      navigate("/avtaler");
    } catch {
      alert("Kunne ikke slette avtale.");
    }
  }

  if (loading) return <div className="text-center py-10 text-gray-500">Laster inn avtale…</div>;
  if (!agreement) return <div className="text-center py-10 text-gray-600">Fant ikke avtalen.</div>;

  const status = getAgreementStatus(agreement.expiryDate);

  // Fakturakunde lagres med en gang avkrysningen endres.
  async function settFakturakunde(verdi) {
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/${agreement._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fakturakunde: verdi }),
      });
      if (!r.ok) throw new Error();
      setAgreement(await r.json());
    } catch {
      alert("Kunne ikke lagre fakturakunde.");
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-8">
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}

      <button onClick={() => navigate("/avtaler")} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
        ← Tilbake til avtaleoversikt
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
            {TYPE_LABELS[agreement.type] || agreement.type}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.cls}`}>{status.label}</span>
          {agreement.fakturakunde && (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-700 text-white">Fakturakunde</span>
          )}
          {agreement.startDate && (
            <span className="text-xs text-gray-600">Inngått {formatDate(agreement.startDate)}</span>
          )}
          {agreement.expiryDate && (
            <span className="text-xs text-gray-600">· Utløper {formatDate(agreement.expiryDate)}</span>
          )}
        </div>

        <div className="mb-4">
          <Logo agreement={agreement} onAgreementUpdated={setAgreement} />
        </div>

        <label
          htmlFor="avtale-faktura"
          className={`mb-4 flex items-center gap-2 rounded-md border-2 p-3 font-semibold ${agreement.fakturakunde ? "border-green-600 bg-green-50 text-green-900" : "border-gray-200 text-gray-800"}`}
        >
          <input
            id="avtale-faktura"
            type="checkbox"
            checked={Boolean(agreement.fakturakunde)}
            onChange={(e) => settFakturakunde(e.target.checked)}
            className="h-5 w-5 accent-green-700"
          />
          Fakturakunde (kan faktureres)
        </label>

        <AgreementInfoForm agreement={agreement} onAgreementUpdated={setAgreement} />

        {exclusionText && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-xs font-semibold text-amber-800">Gjelder ikke på (alle avtaler):</p>
            <p className="text-sm text-amber-800 mt-1 whitespace-pre-wrap">{exclusionText}</p>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-gray-100">
          <ContractImages agreement={agreement} onAgreementUpdated={setAgreement} setLightboxUrl={setLightboxUrl} />
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100">
          <button onClick={handleDelete} className="px-3 py-1.5 rounded-md bg-gray-100 text-red-700 text-xs font-medium hover:bg-gray-200">
            Slett avtale
          </button>
        </div>
      </div>
    </div>
  );
}

export default AvtaleDetalj;
